import { enumMembers, type ApiSchema } from "../../apiSchema";
import {
  readNullableString,
  JsonContractError,
  readArray,
  readBoolean,
  readInteger,
  readObject,
  readOneOf,
  readString,
  type JsonObject,
} from "../../http/jsonDecoding";
import { decodeInputSlot, decodeItemSelector, decodePlaceholderSource, decodeRecord, oneOf, readStrings } from "./selectorDecoders";
import { decodeTranslations, keptMembers, PROCESS_MEMBERS } from "./keptContentMembers";
import { decodeInputDeclaration, decodeOutputDeclaration } from "./declarationDecoders";

/**
 * Step definitions (`dmw-step/2`, processes.md P-1, W-3), decoded whole so the step form
 * can start from a stored version and write it back unchanged where the user changed
 * nothing. A `dmw-step/1` file is read as `/2` by the server already.
 */

export type StepDefinition = ApiSchema<"StepDefinition">;
export type AgentStepDefinition = ApiSchema<"AgentStepDefinition">;
export type HumanStepDefinition = ApiSchema<"HumanStepDefinition">;
export type ToolStepDefinition = ApiSchema<"ToolStepDefinition">;
export type TalkStepDefinition = ApiSchema<"TalkStepDefinition">;

const ITEM_KINDS = enumMembers<ApiSchema<"ProjectItemKind">>({
  ontology_module: true,
  shapes: true,
  competency_question: true,
  few_shot_example: true,
  prompt: true,
  label_list: true,
  step: true,
  workflow: true,
  tool: true,
  skill: true,
  artifact: true,
  capability_overrides: true,
});
const SESSION_MODES = enumMembers<ApiSchema<"SessionMode">>({ thread: true, isolated: true });
export function decodeProjectItemKind(value: unknown): ApiSchema<"ProjectItemKind"> { return oneOf(value, ITEM_KINDS, "item kind"); }
const SKILL_USES = enumMembers<ApiSchema<"SkillUseMode">>({ offered: true, requested: true });
export const ANNOTATION_DESTINATIONS = enumMembers<ApiSchema<"AnnotationDestination">>({ content_change_set: true, knowledge_data: true, thread_log: true });
const COMMENT_RULES = enumMembers<ApiSchema<"CommentRule">>({ optional: true, required: true });
const RELEASE_PARTS = enumMembers<ApiSchema<"ReleasePart">>({ what: true, metadata: true, where: true });
const IMPORT_MODES = enumMembers<ApiSchema<"ImportMode">>({ replace_all: true, upsert: true });

export function decodeStepDefinition(value: unknown): StepDefinition {
  const step = readObject(value, "step definition");
  const kind = readString(step, "kind");
  const kept = keptMembers(step, {
    ...PROCESS_MEMBERS,
    translations: decodeTranslations,
    types: (types) => readStrings(types, "types"),
    derived_from: (sources) => readStrings(sources, "derived from"),
    declared_inputs: (inputs) => readArray(inputs, "declared inputs", decodeInputDeclaration),
    declared_outputs: (outputs) => readArray(outputs, "declared outputs", decodeOutputDeclaration),
  });
  if (kind === "agent") {
    return { ...decodeAgentStep(step), ...kept };
  }
  if (kind === "human") {
    return { ...decodeHumanStep(step), ...kept };
  }
  if (kind === "tool") {
    return { ...decodeToolStep(step), ...kept };
  }
  if (kind === "talk") {
    return { kind, name: readString(step, "name"), description: readString(step, "description"), prompt: readString(step, "prompt"), ...kept };
  }
  throw new JsonContractError('Expected "kind" to be agent, human, tool or talk.');
}

function decodeAgentStep(step: JsonObject): AgentStepDefinition {
  const tools = readObject(step["tools"], "tools");
  const compaction = readObject(step["context_compaction"], "context compaction");
  return {
    kind: "agent",
    name: readString(step, "name"),
    description: readString(step, "description"),
    prompt: readString(step, "prompt"),
    // The step's own agent instructions (WD-1+3 D-2): left out when it has none.
    ...keptMembers(step, { agent_instructions: (instructions) => readString({ agent_instructions: instructions }, "agent_instructions") }),
    placeholders: decodeRecord(step["placeholders"], "placeholders", decodePlaceholderSource),
    inputs: decodeRecord(step["inputs"], "inputs", decodeInputSlot),
    skills: readArray(step["skills"], "skills", (use) => {
      const skill = readObject(use, "skill use");
      return { skill: readString(skill, "skill"), use: readOneOf(skill, "use", SKILL_USES) };
    }),
    tools: {
      dmw: readStrings(tools["dmw"], "DMW tools"),
      item_tools: readStrings(tools["item_tools"], "item tools"),
      skill_servers: readStrings(tools["skill_servers"], "skill servers"),
    },
    output: decodeOutput(step["output"]),
    session: { mode: readOneOf(readObject(step["session"], "session"), "mode", SESSION_MODES) },
    context_compaction: {
      method: readString(compaction, "method"),
      version: readInteger(compaction, "version"),
      parameters: { ...readObject(compaction["parameters"] ?? {}, "compaction parameters") },
    },
    requirements: decodeRequirements(step["requirements"]),
    limits: step["limits"] === null || step["limits"] === undefined ? null : decodeAgentLimits(readObject(step["limits"], "limits")),
  };
}

function decodeOutput(value: unknown): ApiSchema<"OutputContract"> {
  const output = readObject(value, "output");
  const kind = readString(output, "kind");
  if (kind === "text") {
    return { kind };
  }
  if (kind === "annotations") {
    return {
      kind,
      label_list_placeholder: readString(output, "label_list_placeholder"),
      required: readBoolean(output, "required"),
      destination: readOneOf(output, "destination", ANNOTATION_DESTINATIONS),
    };
  }
  if (kind === "change_set") {
    return {
      kind,
      allowed_item_kinds: readArray(output["allowed_item_kinds"], "allowed item kinds", (item) => oneOf(item, ITEM_KINDS, "item kind")),
      required: readBoolean(output, "required"),
    };
  }
  if (kind === "instance_data") {
    return { kind, required: readBoolean(output, "required") };
  }
  throw new JsonContractError('Expected "output.kind" to be text, annotations, instance_data or change_set.');
}

export function decodeRequirements(value: unknown): ApiSchema<"CapabilityRequirement">[] {
  if (value === undefined) {
    return [];
  }
  return readArray(value, "requirements", (item) => {
    const requirement = readObject(item, "requirement");
    const capability = readString(requirement, "capability");
    if (typeof requirement["at_least"] === "number") {
      return { capability, at_least: readInteger(requirement, "at_least") };
    }
    const equals = requirement["equals"];
    if (typeof equals === "boolean" || typeof equals === "string" || (typeof equals === "number" && Number.isInteger(equals))) {
      return { capability, equals };
    }
    throw new JsonContractError('Expected a requirement with "equals" or "at_least".');
  });
}

function decodeAgentLimits(limits: JsonObject): ApiSchema<"StepLimits"> {
  return {
    timeout_seconds: readInteger(limits, "timeout_seconds"),
    maximum_tool_rounds: readInteger(limits, "maximum_tool_rounds"),
    maximum_tool_runs: readInteger(limits, "maximum_tool_runs"),
  };
}

function decodeHumanStep(step: JsonObject): HumanStepDefinition {
  const response = readObject(step["response"], "response");
  const kind = readString(response, "kind");
  let decoded: ApiSchema<"HumanResponse">;
  if (kind === "text") {
    decoded = { kind };
  } else if (kind === "choice") {
    decoded = {
      kind,
      options: readArray(response["options"], "options", (option) => {
        const choice = readObject(option, "option");
        return { key: readString(choice, "key"), label: readString(choice, "label") };
      }),
    };
  } else if (kind === "review_output") {
    // Every stored input review names its comment rule.
    decoded = { kind, ...decodeReviewPart(response), comment: readOneOf(response, "comment", COMMENT_RULES) };
  } else if (kind === "workspace_file") {
    decoded = { kind, suffixes: readStrings(response["suffixes"], "file suffixes") };
  } else if (kind === "review_change_set" || kind === "decide_results" || kind === "review_memory") {
    decoded = { kind, ...decodeReviewPart(response) };
  } else if (kind === "propose_change") {
    decoded = { kind };
  } else if (kind === "release_part") {
    decoded = { kind, part: readOneOf(response, "part", RELEASE_PARTS), input: readNullableString(response, "input") };
  } else if (kind === "keep_memory") {
    const origin = response["origin"];
    decoded = origin === null || origin === undefined ? { kind } : { kind, origin: readString(response, "origin") };
  } else {
    throw new JsonContractError('Expected "response.kind" to be text, choice, review_output, workspace_file, review_change_set, decide_results, propose_change, release_part, keep_memory or review_memory.');
  }
  return {
    kind: "human",
    name: readString(step, "name"),
    description: readString(step, "description"),
    instructions: readString(step, "instructions"),
    inputs: decodeRecord(step["inputs"], "inputs", decodeInputSlot),
    response: decoded,
  };
}

/**
 * A review's common part (proposal 0.8, O1): the input it reviews, and, where given, its comment rule and the talk step it
 * offers; left out where absent, so an edit keeps the file as it was.
 */
function decodeReviewPart(response: JsonObject): { input: string; comment?: ApiSchema<"CommentRule">; talk?: string } {
  const comment = response["comment"];
  const talk = response["talk"];
  return {
    input: readString(response, "input"),
    ...(comment === null || comment === undefined ? {} : { comment: readOneOf(response, "comment", COMMENT_RULES) }),
    ...(talk === null || talk === undefined ? {} : { talk: readString(response, "talk") }),
  };
}

function decodeToolStep(step: JsonObject): ToolStepDefinition {
  const propose = step["propose"];
  const limits = step["limits"];
  return {
    kind: "tool",
    name: readString(step, "name"),
    description: readString(step, "description"),
    tool: readString(step, "tool"),
    inputs: decodeRecord(step["inputs"], "inputs", decodePlaceholderSource),
    input_slots: decodeRecord(step["input_slots"], "input slots", decodeInputSlot),
    parameters: { ...readObject(step["parameters"] ?? {}, "parameters") },
    propose: propose === null || propose === undefined ? null : decodePropose(readObject(propose, "propose")),
    requirements: decodeRequirements(step["requirements"]),
    limits: limits === null || limits === undefined ? null : decodeToolStepLimits(readObject(limits, "limits")),
  };
}

function decodePropose(propose: JsonObject): ApiSchema<"ProposeOutput"> {
  const target = readObject(propose["as"], "proposal target");
  return { output: readString(propose, "output"), title: readString(propose, "title"), as: decodeProposalTarget(target) };
}

/** An item version to propose, or the Thread's bound corpus to replace or update (Q-30). */
function decodeProposalTarget(target: JsonObject): ApiSchema<"AnyProposalTarget"> {
  if (target["kind"] === "corpus") {
    if (target["corpus"] !== "bound") {
      throw new JsonContractError('A corpus proposal names the "bound" corpus.');
    }
    return { kind: "corpus", corpus: "bound", mode: readOneOf(target, "mode", IMPORT_MODES) };
  }
  const item = target["item"];
  return {
    kind: readOneOf(target, "kind", ITEM_KINDS),
    name: readString(target, "name"),
    item: item === "new" ? "new" : decodeItemSelector(item, "proposal item"),
  };
}

function decodeToolStepLimits(limits: JsonObject): ApiSchema<"ToolStepLimits"> {
  const optional = (member: string) => (limits[member] === null || limits[member] === undefined ? null : readInteger(limits, member));
  return { timeout_seconds: optional("timeout_seconds"), memory_megabytes: optional("memory_megabytes"), output_megabytes: optional("output_megabytes") };
}
